import { Interface, JsonRpcApiProvider, getAddress, parseUnits, type JsonRpcPayload, type JsonRpcResult } from 'ethers';
import { memberWalletRpc, type MemberMessageSigner } from './member-wallet-rpc';

export interface MemberWalletAccount extends MemberMessageSigner {
  sendTransaction(call: {chainId:number;to:`0x${string}`;data:`0x${string}`;value:bigint}):Promise<{transactionHash:string}>;
}

/** Existing ethers consumers read through our authenticated paid-RPC boundary. */
export class MemberReadProvider extends JsonRpcApiProvider {
  constructor(private account: MemberMessageSigner, private chainId: number) {
    super(chainId,{staticNetwork:true,batchMaxCount:1});
    this._start();
  }
  async _send(payload: JsonRpcPayload | JsonRpcPayload[]): Promise<JsonRpcResult[]> {
    return Promise.all((Array.isArray(payload)?payload:[payload]).map(async request=>({
      id:request.id,
      result:await memberWalletRpc(this.account,this.chainId,request.method,request.params as unknown[]),
    })));
  }
}

const erc20=new Interface(['function transfer(address,uint256) returns (bool)','function balanceOf(address) view returns (uint256)']);
export async function sendMemberToken(account:MemberWalletAccount, expectedWallet:string, chainId:number, token:string, recipient:string, amount:string, decimals:number) {
  if(getAddress(account.address)!==getAddress(expectedWallet))throw new Error('WALLET_CONTINUITY_MISMATCH');
  if(![1,137,8453].includes(chainId)||!Number.isInteger(decimals)||decimals<0||decimals>36)throw new Error('INVALID_TOKEN_CONFIGURATION');
  const value=parseUnits(amount,decimals);
  if(value<=0n||value>=2n**256n)throw new Error('INVALID_TRANSFER_AMOUNT');
  const result=await account.sendTransaction({chainId,to:getAddress(token) as `0x${string}`,data:erc20.encodeFunctionData('transfer',[getAddress(recipient),value]) as `0x${string}`,value:0n});
  if(!/^0x[0-9a-fA-F]{64}$/.test(result.transactionHash))throw new Error('TX_HASH_MISSING');
  return result;
}

export async function waitForMemberTransaction(account:MemberMessageSigner,chainId:number,hash:string) {
  if(!/^0x[0-9a-fA-F]{64}$/.test(hash))throw new Error('INVALID_TRANSACTION_HASH');
  const deadline=Date.now()+30_000;
  try {
  while(Date.now()<deadline){
    const receipt=await memberWalletRpc(account,chainId,'eth_getTransactionReceipt',[hash]) as null|{status:string;transactionHash:string};
    if(receipt){
      if(receipt.transactionHash.toLowerCase()!==hash.toLowerCase())throw new Error('RECEIPT_MISMATCH');
      if(receipt.status!=='0x1')throw Object.assign(new Error('TRANSACTION_REVERTED'),{transactionHash:hash});
      return hash;
    }
    await new Promise(resolve=>setTimeout(resolve,2000));
  }
  throw new Error('TRANSACTION_PENDING');
  } catch(error) {
    throw Object.assign(error instanceof Error ? error : new Error('CHAIN_DATA_UNAVAILABLE'),{transactionHash:hash});
  }
}
